<template>
  <div class="model-preview">
    <div class="character-setting">{{ $t('home.ModelPreview.characterSetting') }}</div>
    <img v-if="!mirrorStore.isCustomModel"
         :src="mirrorStore.previewModel" />
    <div v-else
         class="crop-wrapper">
      <Vue3DraggableResizable v-if="imageUrl"
                              v-model:x="cropX"
                              v-model:y="cropY"
                              v-model:w="cropWidth"
                              v-model:h="cropHeight"
                              :init-w="initialWidth"
                              :init-h="initialHeight"
                              :draggable="true"
                              :resizable="true"
                              class="draggable-container"
                              @drag-end="handleDragEnd"
                              @resize-end="handleResizeEnd">
        <img ref="imageRef"
             :src="imageUrl"
             class="draggable-image" />
      </Vue3DraggableResizable>
      <div class="svg-overlay">
        <modelCropArea />
      </div>
    </div>
    <a-upload class="uploadModelButton"
              :show-upload-list="false"
              :before-upload="handleBeforeUpload"
              :custom-request="dummyRequest"
              @change="handleChange">
      <a-button class="buttonStyle">{{ $t('home.ModelPreview.addCharacter') }}</a-button>
    </a-upload>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { message } from 'ant-design-vue'
import { useMirrorStore } from '../../../index'
import Vue3DraggableResizable from 'vue3-draggable-resizable'
import 'vue3-draggable-resizable/dist/Vue3DraggableResizable.css'
import modelCropArea from '../mirror/svgPreview.vue'
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const mirrorStore = useMirrorStore()
const imageUrl = ref<string | null>(null)
const cropX = ref(0)
const cropY = ref(0)
const cropWidth = ref(300)
const cropHeight = ref(500)
const imageRef = ref<HTMLImageElement | null>(null)

// 初始宽度和高度
const initialWidth = 300
const initialHeight = 500

const handleBeforeUpload = (file: File) => {
  const isJPG = file.type === 'image/jpeg'
  const isPNG = file.type === 'image/png'
  const isLt2M = (file.size / 1024 / 1024) < 5

  if (!isJPG && !isPNG) {
    message.error(t('home.ModelPreview.error1'))
    return false
  }

  if (!isLt2M) {
    message.error(t('home.ModelPreview.error2'))
    return false
  }

  return true
}

const dummyRequest = (options: any) => {
  const { onSuccess } = options
  setTimeout(() => {
    if (onSuccess) {
      onSuccess('ok')
    }
  }, 0)
}

const handleChange = (info: any) => {
  const file = info.file.originFileObj as File
  const reader = new FileReader()
  reader.onload = () => {
    imageUrl.value = reader.result as string
    mirrorStore.isCustomModel = true
    getCroppedImage()
  }
  reader.readAsDataURL(file)
}

const handleDragEnd = () => {
  getCroppedImage()
}

const handleResizeEnd = () => {
  getCroppedImage()
}

const getCroppedImage = () => {
  const svgElement = document.querySelector('.svg-overlay svg')

  if (imageRef.value && svgElement) {
    const img = imageRef.value
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    // 获取 SVG 的实际尺寸
    const svgRect = svgElement.getBoundingClientRect()

    // 设置 Canvas 与 SVG 裁剪区域一致
    canvas.width = svgRect.width
    canvas.height = svgRect.height

    if (context) {
      // 确认图像实际的偏移位置
      const containerRect = (document.querySelector('.crop-wrapper') as HTMLElement).getBoundingClientRect()

      // 计算相对偏移
      const offsetX = containerRect.left
      const offsetY = containerRect.top

      // 计算源图像的裁剪起点和尺寸
      const sx = (offsetX + cropX.value) - 350
      const sy = (offsetY + cropY.value) - 100
      const sw = cropWidth.value + 200
      const sh = cropHeight.value + 200

      try {
        context.clearRect(0, 0, canvas.width, canvas.height) // 清除之前的内容
        context.save()

        // 创建剪切路径
        context.beginPath()
        context.rect(0, 0, svgRect.width, svgRect.height)
        context.clip()

        // sx, sy是源图像裁剪位置，sw, sh是裁剪宽高
        context.drawImage(
          img as any,
          sx, sy, sw, sh,                   // 从源图像中裁剪出区域
          0, 0, svgRect.width, svgRect.height // 绘制到画布区域
        )

        context.restore()
        const croppedImageDataUrl = canvas.toDataURL('image/png')
        mirrorStore.previewModel = croppedImageDataUrl
        mirrorStore.isCustomModel = true
      } catch (error) {
        console.error(`${t('home.ModelPreview.error2')}:`, error)
      }
    }
  }
}
</script>

<style scoped lang="less">
.model-preview {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;
  position: relative;

  img {
    background-color: #D5E5E6;
    background-size: cover;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}

.character-setting {
  position: absolute;
  z-index: 2500;
  top: 8px;
  left: 8px;
  width: 120px;
  height: 40px;
  font-family: Microsoft YaHei;
  font-weight: bold;
  font-size: 18px;
  color: #0F171C;
  line-height: 36px;
  background-color: #5FCDD0;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 0% 100%);
  padding-left: 16px;
}

.uploadModelButton {
  position: absolute;
  bottom: 30px;
  width: 80%;
  padding: 10px 20px;
  background: linear-gradient(0deg, rgba(27, 72, 70, 0.9), rgba(15, 23, 28, 0.9));
  border: 1px solid rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  text-align: center;
  cursor: pointer;

  .ant-upload {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
  }

  .ant-upload-text {
    color: inherit;
    font-family: inherit;
    font-weight: inherit;
    font-size: inherit;
  }

  .buttonStyle {
    width: 100%;
    font-family: Microsoft YaHei;
    font-weight: bold;
    font-size: 16px;
    border: 0;
    background: none;
    color: #FFFFFF;
  }
}

.uploadModelButton:hover {
  background: linear-gradient(0deg, rgba(27, 72, 70, 1), rgba(15, 23, 28, 1));
}

.crop-wrapper {
  position: relative;
  width: 300px;
  height: calc(100vw * (1000/300));
  max-height: 1000px;
  border: 1px solid #ddd;
  overflow: hidden;
}

.draggable-image {
  position: absolute;
  max-width: none;
  width: auto;
  height: 100%;
}

.svg-overlay {
  pointer-events: none;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1000;
  /* 确保 SVG 组件在最上层 */
  top: 0;
  left: 0;
}

.human-shape {
  width: 100%;
  height: 100%;
}

.draggable-container {
  border: 1px dashed #000;
  position: absolute;
  z-index: 999;
}
</style>
